import { LoaderCircle } from "lucide-react";

import { cn } from "../lib/utils";
import { Skeleton } from "./ui/skeleton";

/** Reserve the content's shape; never delay a completed request to finish an animation. */
export function ContentLoading({
  label,
  variant = "rows",
  className,
}: {
  label: string;
  variant?: "rows" | "cards" | "document";
  className?: string;
}) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      aria-label={label}
      role="status"
      className={cn("min-w-0 py-4", className)}
    >
      <span className="sr-only">{label}</span>
      <div aria-hidden="true" className="loading-placeholder">
        {variant === "document" ? (
          <div className="space-y-8">
            <div className="space-y-4">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-10 w-2/3 max-w-96" />
            </div>
            <Skeleton className="h-8 w-full max-w-sm" />
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            <div className="space-y-3">
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        ) : (
          <div
            className={
              variant === "cards" ? "grid gap-4 sm:grid-cols-2 xl:grid-cols-3" : "space-y-3"
            }
          >
            {[0, 1, 2].map((index) => (
              <div className="space-y-3 rounded-sm border bg-card p-4" key={index}>
                <Skeleton className="h-4 w-2/5 max-w-48" />
                <Skeleton className="h-3 w-3/4 max-w-sm" />
                {variant === "cards" ? <Skeleton className="mt-5 h-8 w-1/3" /> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function InlineLoading({ label, className }: { label: string; className?: string }) {
  return (
    <span
      role="status"
      className={cn("inline-flex items-center gap-2 py-2 text-sm text-muted-foreground", className)}
    >
      <LoaderCircle
        aria-hidden="true"
        className="size-4 shrink-0 animate-spin motion-reduce:animate-none"
      />
      {label}
    </span>
  );
}
